import { cn } from "../../lib/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { Skeleton } from "../../components/ui/skeleton";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarXIcon,
  ListIcon,
  Loader2
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { usePlanOfCareStore } from "../../stores/planOfCareStore";
import {
  PlanOfCareDetails,
  usePlanOfCareDetails
} from "../../hooks/usePlanOfCareDetails";
import { Card, CardContent, CardFooter } from "../../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { parseAsInteger, useQueryState } from "nuqs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "../../components/ui/alert-dialog";
import axios from "axios";
import { toast } from "sonner";

export function AppointmentsSection() {
  const {
    uniqueId,
    currentPeriod,
    setCurrentPeriod,
    setSelectedAppointment,
    bookingOutcomes,
    authorization,
    authorizationsByType,
    scheduledVisitDates,
    ignoreAuthorization,
    caseStudyId
  } = usePlanOfCareStore();

  const { data, isLoading, refetch } = usePlanOfCareDetails({
    uniqueId: uniqueId,
    enabled: Boolean(uniqueId)
  });

  const [week] = useQueryState("week", parseAsInteger);
  const [poc] = useQueryState("poc", parseAsInteger);
  const [, setCancelVisit] = useQueryState("cancel_visit");

  const cancelEnabled = Boolean((data as PlanOfCareDetails).agent.cancelEnabled);
  const chargeCancelFee = Boolean((data as PlanOfCareDetails).agent.chargeCancellationFee);
  const cancellationFeeWindow = (data as PlanOfCareDetails).agent.cancellationFee?.window;
  const cancellationFeeAmount = (data as PlanOfCareDetails).agent.cancellationFee?.amount;
  const noCancellationWindowHours = (data as PlanOfCareDetails).agent.noCancellationWindowHours;

  const isAuthorizationBlocked = useMemo(() => {
    if (ignoreAuthorization || !currentPeriod) return false;
    if (authorization === undefined && authorizationsByType === undefined) return true;
    const start = DateTime.fromISO(currentPeriod.periodStart.slice(0, 10));
    const days = Array.from({ length: 7 }, (_, i) => start.plus({ days: i }));
    return !days.some((day) => {
      const dayKey = day.toISODate();
      if (!dayKey) return false;

      if (authorization !== undefined) {
        if (dayKey < authorization.startDate || dayKey > authorization.endDate) return false;
        const used = scheduledVisitDates.filter(d => d >= authorization.startDate && d <= authorization.endDate).length
          + bookingOutcomes.filter(o => {
            if (!o.start || o.caseStudyId !== caseStudyId) return false;
            const k = o.start.toISODate();
            return k ? k >= authorization.startDate && k <= authorization.endDate : false;
          }).length;
        if (used >= authorization.visitLimit) return false;
      }

      if (authorizationsByType !== undefined) {
        const allTypesPass = Object.values(authorizationsByType).every((ranges) =>
          ranges.length === 0 || ranges.some((range) => {
            if (dayKey < range.startDate || dayKey > range.endDate) return false;
            const used = scheduledVisitDates.filter(d => d >= range.startDate && d <= range.endDate).length
              + bookingOutcomes.filter(o => {
                if (!o.start || o.caseStudyId !== caseStudyId) return false;
                const k = o.start.toISODate();
                return k ? k >= range.startDate && k <= range.endDate : false;
              }).length;
            return range.visitLimit === null || used < range.visitLimit;
          })
        );
        if (!allTypesPass) return false;
      }

      return true;
    });
  }, [currentPeriod, authorization, authorizationsByType, scheduledVisitDates, ignoreAuthorization, bookingOutcomes, caseStudyId]);

  const unscheduledAppointments = useMemo(
    () =>
      currentPeriod?.appointments.filter(
        (appointment) =>
          (appointment.status === "unscheduled" || (appointment.status === "unauthorized" && !isAuthorizationBlocked)) &&
          !bookingOutcomes.some((outcome) => outcome.id === appointment.id)
      ),
    [currentPeriod, bookingOutcomes, isAuthorizationBlocked]
  );

  useEffect(() => {
    if (data && week !== null && poc !== null && poc !== undefined) {
      // Convert 1-indexed week to 0-indexed array index
      const weekIndex = week - 1;
      setCurrentPeriod(
        (data as PlanOfCareDetails).planOfCares[poc].periods[weekIndex]
      );
    }
  }, [data, week, poc, setCurrentPeriod]);

  useEffect(() => {
    if (unscheduledAppointments && unscheduledAppointments.length > 0) {
      setSelectedAppointment({
        id: unscheduledAppointments[0].id,
        appointmentTypeId: unscheduledAppointments[0].appointmentTypeId,
        appointmentTypeName: unscheduledAppointments[0].appointmentTypeName,
        appointmentDuration: unscheduledAppointments[0].appointmentDuration || 0
      });
    }
  }, [unscheduledAppointments, setSelectedAppointment]);

  return (
    <div className="py-4 xl:py-8 border-t xl:border-l xl:border-t-0 border-border flex items-start shrink-0">
      <div className="flex flex-col gap-4 w-full">
        <WeekHeader isLoading={isLoading} />
        <Appointments isLoading={isLoading} onAppointmentCancelled={refetch} cancelEnabled={cancelEnabled} chargeCancelFee={chargeCancelFee} cancellationFeeWindow={cancellationFeeWindow} cancellationFeeAmount={cancellationFeeAmount} noCancellationWindowHours={noCancellationWindowHours} isAuthorizationBlocked={isAuthorizationBlocked} />
        <div className="px-8">
          <Button size="sm" variant="ghost" className="text-muted-foreground gap-1.5 -ml-2" onClick={() => setCancelVisit("all")}>
            <ListIcon className="size-3.5" />
            View all upcoming appointments
          </Button>
        </div>
      </div>
    </div>
  );
}

enum StatusStrings {
  scheduled = "Scheduled",
  unscheduled = "Unscheduled",
  missed = "Missed",
  unauthorized = "Locked",
  occurred = "Completed"
}

enum StatusBadgeVariants {
  scheduled = "info",
  unscheduled = "warning",
  missed = "destructive",
  unauthorized = "default",
  occurred = "success"
}

enum StatusDotColors {
  scheduled = "bg-blue-500",
  unscheduled = "bg-amber-500",
  missed = "bg-red-500",
  unauthorized = "bg-neutral-500",
  occurred = "bg-green-500"
}

enum BookingOutcomeVariants {
  Scheduled = "info",
  "Joined Waitlist" = "info",
  Skipped = "destructive"
}

enum BookingOutcomeDotColors {
  Scheduled = "bg-blue-500",
  "Joined Waitlist" = "bg-blue-500",
  Skipped = "bg-red-500"
}

function WeekHeader({ isLoading }: { isLoading: boolean }) {
  const today = DateTime.now();

  const { currentPeriod, totalWeeks, setDate, setBookableDays } = usePlanOfCareStore();

  const [week, setWeek] = useQueryState("week", parseAsInteger);

  return (
    <div className="flex items-center justify-between px-8 gap-4">
      <div className="flex items-baseline gap-1">
        <span className="text-lg font-semibold text-foreground flex items-center gap-1">
          {currentPeriod
            ? `${DateTime.fromISO(currentPeriod.periodStart).toFormat(
                "MMM d"
              )} - ${DateTime.fromISO(currentPeriod.periodEnd).toFormat(
                "MMM d"
              )},`
            : today.toFormat("MMM d,")}
        </span>
        <span className="text-lg font-normal text-muted-foreground">
          {currentPeriod
            ? DateTime.fromISO(currentPeriod.periodStart).toFormat("yyyy")
            : today.toFormat("yyyy")}
        </span>
      </div>
      <div className="flex items-center gap-1">
      <Button
        variant="secondary"
        size="sm"
        disabled={isLoading || week === 1}
        onClick={() => {
          setWeek(Math.max(1, (week || 1) - 1));
          setDate(undefined);
          setBookableDays([]);
        }}
        className="text-xs gap-0 pr-2 pl-1"
      >
        <ChevronLeftIcon /> Prev
      </Button>
      <Button
        variant="secondary"
        size="sm"
        disabled={isLoading || week === totalWeeks}
        onClick={() => {
          setWeek(Math.min(totalWeeks, (week || 1) + 1));
          setDate(undefined);
          setBookableDays([]);
        }}
        className="text-xs gap-0 pr-1 pl-2"
      >
        Next <ChevronRightIcon />
      </Button>
    </div>
    </div>
  );
}

function Appointments({
  isLoading,
  onAppointmentCancelled,
  cancelEnabled,
  chargeCancelFee,
  cancellationFeeWindow,
  cancellationFeeAmount,
  noCancellationWindowHours,
  isAuthorizationBlocked
}: {
  isLoading: boolean;
  onAppointmentCancelled: () => Promise<unknown>;
  cancelEnabled: boolean;
  chargeCancelFee: boolean;
  cancellationFeeWindow: "Always" | number | undefined;
  cancellationFeeAmount: number | undefined;
  noCancellationWindowHours: number | undefined;
  isAuthorizationBlocked: boolean;
}) {
  const { currentPeriod, selectedAppointment, setSelectedAppointment } =
    usePlanOfCareStore();

  const appointments = useMemo(
    () =>
      currentPeriod?.appointments.filter(
        (appointment) => appointment.status !== "missed"
      ),
    [currentPeriod]
  );

  return (
    <div className="flex flex-col xl:h-[352px] gap-4 relative">
      <RadioGroup
        value={selectedAppointment.id}
        onValueChange={(value) => {
          const appointment = currentPeriod?.appointments.find(
            (appointment) => appointment.id === value
          );
          setSelectedAppointment({
            id: value,
            appointmentTypeId: appointment?.appointmentTypeId || "",
            appointmentTypeName: appointment?.appointmentTypeName || "",
            appointmentDuration: appointment?.appointmentDuration || 0
          });
        }}
        className="flex flex-col gap-2 xl:overflow-y-auto px-6"
      >
        {isLoading &&
          Array.from({ length: 2 }).map((_, index) => (
            <Skeleton
              key={index}
              className="min-w-[312px] w-full h-[62px] rounded-md"
            />
          ))}
        {!isLoading &&
          appointments &&
          appointments.map((appointment) => (
            <AppointmentCard
              key={appointment.id}
              appointment={appointment}
              onAppointmentCancelled={onAppointmentCancelled}
              cancelEnabled={cancelEnabled}
              chargeCancelFee={chargeCancelFee}
              cancellationFeeWindow={cancellationFeeWindow}
              cancellationFeeAmount={cancellationFeeAmount}
              noCancellationWindowHours={noCancellationWindowHours}
              isAuthorizationBlocked={isAuthorizationBlocked}
            />
          ))}
        {!isLoading &&
          currentPeriod?.extraAppointments.map((appointment) => (
            <AppointmentCard
              key={appointment.id.toString()}
              appointment={appointment}
              onAppointmentCancelled={onAppointmentCancelled}
              cancelEnabled={cancelEnabled}
              chargeCancelFee={chargeCancelFee}
              cancellationFeeWindow={cancellationFeeWindow}
              cancellationFeeAmount={cancellationFeeAmount}
              noCancellationWindowHours={noCancellationWindowHours}
              isAuthorizationBlocked={isAuthorizationBlocked}
            />
          ))}
        {appointments &&
          appointments.length === 0 &&
          currentPeriod?.extraAppointments &&
          currentPeriod?.extraAppointments.length === 0 && (
            <div className="py-2 px-3">
              <div className="min-w-[290px] w-full">
                <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground py-8 xl:py-12">
                  <div className="flex items-center justify-center size-10 rounded-full bg-accent">
                    <CalendarXIcon
                      className="size-5 text-muted-foreground"
                      aria-hidden="true"
                    />
                  </div>
                  <p className="text-sm text-center">
                    No appointments this week
                  </p>
                </div>
              </div>
            </div>
          )}
      </RadioGroup>
    </div>
  );
}

function AppointmentCard({
  appointment,
  onAppointmentCancelled,
  cancelEnabled,
  chargeCancelFee,
  cancellationFeeWindow,
  cancellationFeeAmount,
  noCancellationWindowHours,
  isAuthorizationBlocked
}: {
  appointment:
    | PlanOfCareDetails["planOfCares"][0]["periods"][0]["appointments"][0]
    | PlanOfCareDetails["planOfCares"][0]["periods"][0]["extraAppointments"][0];
  onAppointmentCancelled: () => Promise<unknown>;
  cancelEnabled: boolean;
  chargeCancelFee: boolean;
  cancellationFeeWindow: "Always" | number | undefined;
  cancellationFeeAmount: number | undefined;
  noCancellationWindowHours: number | undefined;
  isAuthorizationBlocked: boolean;
}) {
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const {
    uniqueId,
    selectedAppointment,
    bookingOutcomes,
    appendBookingOutcomes,
    removeBookingOutcome
  } = usePlanOfCareStore();

  const bookingOutcome = useMemo(() => {
    return bookingOutcomes.find((outcome) => outcome.id === appointment.id);
  }, [bookingOutcomes, appointment.id]);

  const cancellationDate = useMemo(() => {
    if (bookingOutcome?.status === "Scheduled" && bookingOutcome.start) {
      return bookingOutcome.start.toISO();
    }

    if (appointment.status === "scheduled" && appointment.start) {
      return appointment.start;
    }

    return null;
  }, [appointment.start, appointment.status, bookingOutcome]);

  const withinNoCancellationWindow = useMemo(() => {
    if (
      typeof noCancellationWindowHours !== "number" ||
      noCancellationWindowHours <= 0 ||
      !cancellationDate
    ) {
      return false;
    }
    const hoursUntilAppointment = DateTime.fromISO(cancellationDate)
      .diff(DateTime.now(), "hours")
      .hours;
    return hoursUntilAppointment < noCancellationWindowHours;
  }, [noCancellationWindowHours, cancellationDate]);

  const canCancelScheduledAppointment = useMemo(
    () =>
      Boolean(cancellationDate) &&
      (appointment.status === "scheduled" ||
        bookingOutcome?.status === "Scheduled") &&
      !withinNoCancellationWindow,
    [appointment.status, bookingOutcome, cancellationDate, withinNoCancellationWindow]
  );

  const cancellationDateLabel = useMemo(() => {
    if (!cancellationDate) {
      return "";
    }

    return DateTime.fromISO(cancellationDate).toFormat("EEEE, MMMM d 'at' h:mm a");
  }, [cancellationDate]);

  const requiresCancellationFee = useMemo(() => {
    if (!chargeCancelFee || !cancellationDate) {
      return false;
    }

    if (cancellationFeeWindow === "Always") {
      return true;
    }

    if (typeof cancellationFeeWindow === "number") {
      const hoursUntilAppointment = DateTime.fromISO(cancellationDate)
        .diff(DateTime.now(), "hours")
        .hours;

      return hoursUntilAppointment >= 0 && hoursUntilAppointment <= cancellationFeeWindow;
    }

    return false;
  }, [chargeCancelFee, cancellationDate, cancellationFeeWindow]);

  const cancellationFeeLabel = useMemo(() => {
    if (typeof cancellationFeeAmount !== "number") {
      return "";
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD"
    }).format(cancellationFeeAmount / 100);
  }, [cancellationFeeAmount]);

  const cancellationDialogDescription = useMemo(() => {
    const baseDescription = `This will cancel your scheduled appointment${cancellationDateLabel ? ` on ${cancellationDateLabel}` : ""}.`;

    if (!requiresCancellationFee) {
      return baseDescription;
    }

    return `${baseDescription} This cancellation requires a late fee${cancellationFeeLabel ? ` of ${cancellationFeeLabel}` : ""}.`;
  }, [cancellationDateLabel, requiresCancellationFee, cancellationFeeLabel]);

  const handleCancelAppointment = useCallback(async () => {
    if (!uniqueId || !cancellationDate) {
      toast.error("Unable to cancel this appointment right now. Please try again.");
      return;
    }

    setIsCancelling(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/bookings/cancel`,
        {
          uniqueId: uniqueId,
          appointmentDate: cancellationDate,
          reason: "Cancelled by patient from plan-of-care booking page",
          returnUrl: window.location.href
        }
      );

      const {
        requiresPayment,
        checkoutUrl,
        message
      }: {
        requiresPayment?: boolean;
        checkoutUrl?: string;
        message?: string;
      } = response.data || {};

      if (requiresPayment && checkoutUrl) {
        window.location.assign(checkoutUrl);
        return;
      }

      removeBookingOutcome(String(appointment.id));
      await onAppointmentCancelled();
      setIsCancelDialogOpen(false);
      toast.success(message || "Appointment cancelled successfully.");
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          "There was an error cancelling your appointment. Please try again."
      );
    } finally {
      setIsCancelling(false);
    }
  }, [
    uniqueId,
    cancellationDate,
    removeBookingOutcome,
    appointment.id,
    onAppointmentCancelled
  ]);

  return (
    <Card
      className={cn(
        "py-2 relative gap-1",
        selectedAppointment.id === appointment.id &&
          "border-primary/60 bg-primary/5",
        bookingOutcome && "border-emerald-500/60 bg-emerald-500/5"
      )}
    >
      <CardContent className="px-3">
        <div className="flex items-center justify-between gap-3 min-w-72 w-full">
          <div className="flex items-center gap-3 w-full">
            <p className="text-base font-medium tracking-tight truncate">
              {appointment.appointmentTypeName}
            </p>
            <Badge
              variant={
                bookingOutcome
                  ? BookingOutcomeVariants[bookingOutcome.status]
                  : StatusBadgeVariants[
                      (appointment.status === "unauthorized" && !isAuthorizationBlocked) ? "unscheduled"
                      : (appointment.status === "unscheduled" && isAuthorizationBlocked) ? "unauthorized"
                      : appointment.status
                    ]
              }
            >
              <span
                className={cn(
                  "inline-block size-1.5 rounded-full",
                  bookingOutcome
                    ? BookingOutcomeDotColors[bookingOutcome.status]
                    : StatusDotColors[
                        (appointment.status === "unauthorized" && !isAuthorizationBlocked) ? "unscheduled"
                        : (appointment.status === "unscheduled" && isAuthorizationBlocked) ? "unauthorized"
                        : appointment.status
                      ]
                )}
              />
              {bookingOutcome
                ? bookingOutcome.status
                : StatusStrings[
                    (appointment.status === "unauthorized" && !isAuthorizationBlocked) ? "unscheduled"
                    : (appointment.status === "unscheduled" && isAuthorizationBlocked) ? "unauthorized"
                    : appointment.status
                  ]}
            </Badge>
          </div>
          {((appointment.status === "unscheduled" || appointment.status === "unauthorized") && !isAuthorizationBlocked) && !bookingOutcome && (
            <RadioGroupItem
              value={appointment.id}
              id={appointment.id}
              aria-label="Select appointment"
              className="order-1 after:absolute after:inset-0"
            />
          )}
        </div>
        {bookingOutcome?.providerName && (
          <p className="text-xs text-muted-foreground">
            {bookingOutcome.providerName}
          </p>
        )}
        <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
          {appointment.status !== "unscheduled" && appointment.start ? (
            <>
              <span>
                {DateTime.fromISO(appointment.start).toFormat("MMM d, yyyy")}
              </span>
              <span className="inline-block size-1 rounded-full bg-muted-foreground/50" />
              <span>
                {DateTime.fromISO(appointment.start).toFormat("h:mm a")}
              </span>
              <span className="inline-block size-1 rounded-full bg-muted-foreground/50" />
              <span>{appointment.appointmentDuration} min</span>
            </>
          ) : bookingOutcome?.start ? (
            <>
              <span>{bookingOutcome.start.toFormat("MMM d, yyyy")}</span>
              <span className="inline-block size-1 rounded-full bg-muted-foreground/50" />
              <span>{bookingOutcome.start.toFormat("h:mm a")}</span>
              <span className="inline-block size-1 rounded-full bg-muted-foreground/50" />
              <span>{bookingOutcome.appointmentDuration} min</span>
            </>
          ) : (
            <span>{appointment.appointmentDuration} min</span>
          )}
        </div>
      </CardContent>
      {cancelEnabled && canCancelScheduledAppointment && (
        <CardFooter className="pt-0 px-2 justify-end">
          <AlertDialog
            open={isCancelDialogOpen}
            onOpenChange={setIsCancelDialogOpen}
          >
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="z-50 !border-red-500/30 text-red-900 dark:text-red-200 bg-red-500/10 hover:bg-red-500/20 hover:text-red-900 dark:hover:text-red-200"
                disabled={isCancelling}
              >
                Cancel
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Cancel appointment?</AlertDialogTitle>
                <AlertDialogDescription>
                  {cancellationDialogDescription}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isCancelling}>
                  Keep appointment
                </AlertDialogCancel>
                <AlertDialogAction asChild>
                  <Button
                    variant="destructive"
                    disabled={isCancelling}
                    onClick={(e) => {
                      e.preventDefault();
                      handleCancelAppointment();
                    }}
                  >
                    {isCancelling && <Loader2 className="size-4 animate-spin" />}
                    Confirm cancel
                  </Button>
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardFooter>
      )}
      {selectedAppointment.id === appointment.id && !bookingOutcome && <CardFooter className="pt-0 px-2 justify-end">
        <Button size="sm" className="z-50" onClick={(e) => {
          e.preventDefault();
          appendBookingOutcomes({
            status: "Skipped",
            id: selectedAppointment.id,
            appointmentTypeId: selectedAppointment.appointmentTypeId,
            appointmentTypeName: selectedAppointment.appointmentTypeName,
            appointmentDuration: selectedAppointment.appointmentDuration
          });
        }}>
          Skip appointment
        </Button>
      </CardFooter>}
    </Card>
  );
}
